/**
 * Sort option item presentation component.
 *
 * Renders a single row inside the sort action sheet with a label
 * and a radio circle indicator on the right. No dividers.
 * @module features/scouting/components/MobileTargetsToolbar/SortOptionItem/SortOptionItemView
 */

import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/** Theme key derived from isDark prop */
type RadioTheme = "dark" | "light";

/** Active radio ring class per theme */
const RADIO_ACTIVE_CLASS: Record<RadioTheme, string> = {
  light:
    "h-5 w-5 items-center justify-center rounded-full border-2 border-outline-emphasis",
  dark: "h-5 w-5 items-center justify-center rounded-full border-2 border-white",
};

/** Inactive radio ring class per theme */
const RADIO_INACTIVE_CLASS: Record<RadioTheme, string> = {
  light:
    "h-5 w-5 items-center justify-center rounded-full border-2 border-outline-strong",
  dark: "h-5 w-5 items-center justify-center rounded-full border-2 border-outline-strong",
};

/** Active radio dot class per theme */
const RADIO_DOT_CLASS: Record<RadioTheme, string> = {
  light: "h-3 w-3 rounded-full bg-outline-emphasis",
  dark: "h-3 w-3 rounded-full bg-white",
};

/** Props for SortOptionItemView */
interface SortOptionItemViewProps {
  /** Display label for the sort option */
  readonly label: string;
  /** Whether this sort option is currently selected */
  readonly isActive: boolean;
  /** Callback when the option is tapped */
  readonly onPress: () => void;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Optional testID forwarded to the row Pressable (e2e) */
  readonly testID?: string;
}

/**
 * Renders a single sort option row with radio indicator.
 * @param root0 - Sort option display data and press handler
 * @param root0.label - Sort option label
 * @param root0.isActive - Whether this option is selected
 * @param root0.onPress - Press handler
 * @param root0.isDark - Dark mode flag
 * @param root0.testID - Optional testID forwarded to the row Pressable
 */
const SortOptionItemView = ({
  label,
  isActive,
  onPress,
  isDark,
  testID,
}: SortOptionItemViewProps) => (
  <Pressable
    testID={testID}
    onPress={onPress}
    className="flex-row items-center justify-between py-3"
  >
    <Text variant="unstyled" className="text-title-sm text-content-primary">
      {label}
    </Text>
    <Box
      className={
        isActive
          ? RADIO_ACTIVE_CLASS[isDark ? "dark" : "light"]
          : RADIO_INACTIVE_CLASS[isDark ? "dark" : "light"]
      }
    >
      {isActive ? (
        <Box className={RADIO_DOT_CLASS[isDark ? "dark" : "light"]} />
      ) : null}
    </Box>
  </Pressable>
);

SortOptionItemView.displayName = "SortOptionItemView";

export default memo(SortOptionItemView);
